import { View, Text } from 'react-native'
import { Pressable } from '@/components/ui/Pressable'
import { ExternalLink } from '@/components/ui/icons'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, INTERACTION } from '@/lib/theme/tokens'
import { FONTS, RADIUS, SPACING } from '@/lib/brand'
import { formatPrice } from '@/lib/utils/format'
import type { TourSplitShare } from '@/lib/types/tours.types'

export function CheckoutSplitBoard({
  shares,
  onShare,
  onOpen,
  shareLabel,
  openLabel,
  partLabel,
  paidLabel,
  pendingLabel,
}: {
  shares: TourSplitShare[]
  onShare: (url: string) => void
  onOpen: (token: string) => void
  shareLabel: string
  openLabel: string
  partLabel: (n: number) => string
  paidLabel: string
  pendingLabel: string
}) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const settled = shares.filter((share) => share.status === 'settled').length

  return (
    <View testID="tours.split-board" style={{ gap: SPACING.xs }}>
      <Text
        style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textSecondary }}
        testID="tours.split-progress"
      >
        {settled}/{shares.length}
      </Text>
      {shares.map((share, index) => {
        const paid = share.status === 'settled'
        return (
          <View
            key={share.shareToken}
            testID={`tours.split-row-${index + 1}`}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              minHeight: INTERACTION.minTouchTarget,
              paddingVertical: SPACING.xs,
              borderTopWidth: index === 0 ? 0 : 1,
              borderTopColor: colors.border,
              gap: SPACING.sm,
            }}
          >
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text
                style={{ fontFamily: FONTS.body, fontSize: 14, color: colors.textPrimary }}
                numberOfLines={1}
              >
                {partLabel(index + 1)} · {formatPrice(share.amountCents)}
              </Text>
              <Text
                style={{
                  marginTop: 2,
                  fontFamily: FONTS.body,
                  fontSize: 12,
                  color: paid ? colors.success : colors.textMuted,
                }}
              >
                {paid ? paidLabel : pendingLabel}
              </Text>
            </View>
            <Pressable
              onPress={() => onShare(share.shareUrl)}
              accessibilityRole="button"
              accessibilityLabel={shareLabel}
              testID={`tours.split-share-${index + 1}`}
              style={{
                minHeight: INTERACTION.minTouchTarget,
                justifyContent: 'center',
                paddingHorizontal: SPACING.sm,
              }}
            >
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 13, color: colors.textAccent }}>
                {shareLabel}
              </Text>
            </Pressable>
            <Pressable
              onPress={() => onOpen(share.shareToken)}
              accessibilityRole="button"
              accessibilityLabel={openLabel}
              testID={`tours.split-open-${index + 1}`}
              style={{
                width: INTERACTION.minTouchTarget,
                height: INTERACTION.minTouchTarget,
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: RADIUS.full,
              }}
            >
              <ExternalLink size={18} color={colors.textSecondary} strokeWidth={1.8} />
            </Pressable>
          </View>
        )
      })}
    </View>
  )
}
